import { ContestStatus } from "@/generated/graphql";

/**
 * Single source of truth for the "is this ended raffle still shown" rule.
 *
 * An ended raffle is only shown once it has a winner AND a prize AND an
 * end_time — the three fields the finished-raffle card needs to render its
 * winner state. Both consumers derive their visibility from here so they never
 * drift apart:
 *   - raffle-item-card.vue's root v-if (the card hides itself when ended-but-
 *     incomplete), and
 *   - the channel page's quests-completed counter (use-channel-page.ts), whose
 *     denominator must match exactly what the rail renders.
 *
 * The parameter types are the minimal structural shape both call sites satisfy
 * (the masked RaffleItemCardData fragment result and the unmasked Raffle branch
 * of ContestsForChannel), so neither has to expose its concrete generated type.
 */

/** True when an ended raffle has the winner, prize and end_time to render as finished. */
export function hasRaffleFinishedData(raffle: {
  end_time?: string | null;
  winner_users?: readonly unknown[] | null;
  prize?: unknown;
}): boolean {
  return Boolean(raffle.winner_users?.[0] && raffle.prize && raffle.end_time);
}

/**
 * True when a raffle should appear in the rail: any non-ended raffle is always
 * shown; an ended raffle is only shown once its finished data is complete.
 */
export function isRaffleVisible(raffle: {
  status: ContestStatus;
  end_time?: string | null;
  winner_users?: readonly unknown[] | null;
  prize?: unknown;
}): boolean {
  return raffle.status !== ContestStatus.Ended || hasRaffleFinishedData(raffle);
}
